import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, DatePicker, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { doctorApi } from '../api/patient';
import { PatientInfo, CreatePatientRequest } from '../api/patient';
import { appointmentApi, CreateAppointmentRequest } from '../api/appointment';
import { scheduleApi } from '../api/schedule';
import dayjs from 'dayjs';
import { DoctorSchedule } from '../api/model';

const { Option } = Select;

const Patients: React.FC = () => {
  const [patients, setPatients] = useState<PatientInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [createAppointmentModalVisible, setCreateAppointmentModalVisible] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientInfo | null>(null);
  const [availableSchedules, setAvailableSchedules] = useState<DoctorSchedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [form] = Form.useForm();
  const [appointmentForm] = Form.useForm();
  const navigate = useNavigate();
  
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 获取患者列表
  const fetchPatients = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const response = await doctorApi.listPatients({ page, page_size: size });
      if (response.success) {
        setPatients(response.data.data);
        setTotal(response.data.total);
        setCurrentPage(response.data.page);
        setPageSize(response.data.page_size);
      } else {
        message.error(response.message || '获取患者列表失败');
      }
    } catch (error) {
      console.error('获取患者列表失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handlePageChange = (page: number, size?: number) => {
    fetchPatients(page, size || pageSize);
  };

  // 处理页面大小变化
  const handlePageSizeChange = (current: number, size: number) => {
    setPageSize(size);
    fetchPatients(1, size);
  };

  // 提交表单
  const handleSubmit = async (values: any) => {
    try {
      // 构造请求数据
      const requestData: CreatePatientRequest = {
        name: values.name,
        phone_number: values.phone_number,
        gender: values.gender,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : undefined
      };

      const response = await doctorApi.createPatient(requestData);
      if (response.success) {
        message.success('患者创建成功');
        setModalVisible(false);
        form.resetFields();
        // 重新获取患者列表
        fetchPatients(1, pageSize);
      } else {
        message.error(response.message || '创建患者失败');
      }
    } catch (error) {
      console.error('创建患者失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 获取可用的医生排班
  const fetchAvailableSchedules = async () => {
    setSchedulesLoading(true);
    try {
      // 获取未来一周的排班
      const startDate = dayjs().toISOString();
      const endDate = dayjs().add(7, 'day').toISOString();
      
      const response = await scheduleApi.listDoctorSchedules({
        start_time: startDate,
        end_time: endDate
      });
      
      if (response.success) {
        setAvailableSchedules(response.data);
      } else {
        message.error(response.message || '获取医生排班失败');
      }
    } catch (error) {
      console.error('获取医生排班失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setSchedulesLoading(false);
    }
  };

  // 打开创建预约模态框
  const openCreateAppointmentModal = (patient: PatientInfo) => {
    setSelectedPatient(patient);
    setCreateAppointmentModalVisible(true);
    fetchAvailableSchedules();
    // 重置表单
    appointmentForm.resetFields();
  };

  // 提交预约表单
  const handleAppointmentSubmit = async (values: any) => {
    if (!selectedPatient) return;
    
    try {
      const requestData: CreateAppointmentRequest = {
        patient_id: selectedPatient.id,
        doctor_schedule_id: values.doctor_schedule_id,
        description: values.description
      };

      const response = await appointmentApi.createAppointment(requestData);
      if (response.success) {
        message.success('预约创建成功');
        setCreateAppointmentModalVisible(false);
        appointmentForm.resetFields();
        setSelectedPatient(null);
      } else {
        message.error(response.message || '创建预约失败');
      }
    } catch (error) {
      console.error('创建预约失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 初始化获取数据
  useEffect(() => {
    fetchPatients();
  }, []);

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender: string) => gender || '--',
    },
    {
      title: '出生日期',
      dataIndex: 'birth_date',
      key: 'birth_date',
      render: (birthDate: string) => birthDate || '--',
    },
    {
      title: '手机号',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phoneNumber: string) => phoneNumber || '--',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: PatientInfo) => (
        <>
          <Button 
            type="link" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/patients/${record.id}/treatment-records`, { state: { patientName: record.name } });
            }}
          >
            治疗记录
          </Button>
          <Button 
            type="link" 
            onClick={(e) => {
              e.stopPropagation();
              openCreateAppointmentModal(record);
            }}
          >
            新增预约
          </Button>
        </>
      ),
    },
  ];

  return (
    <div>
      <Card 
        title="患者列表" 
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setModalVisible(true)}
          >
            新建患者
          </Button>
        }
      >
        <Table
          dataSource={patients}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: handlePageChange,
            onShowSizeChange: handlePageSizeChange,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
        />
      </Card>

      {/* 创建患者模态框 */}
      <Modal
        title="新建患者"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入患者姓名' }]}
          >
            <Input placeholder="请输入患者姓名" />
          </Form.Item>

          <Form.Item
            name="phone_number"
            label="手机号"
            rules={[{ required: true, message: '请输入手机号' }]}
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>

          <Form.Item name="gender" label="性别">
            <Select placeholder="请选择性别">
              <Option value="男">男</Option>
              <Option value="女">女</Option>
            </Select>
          </Form.Item>

          <Form.Item name="birth_date" label="出生日期">
            <DatePicker placeholder="请选择出生日期" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建预约模态框 */}
      <Modal
        title={`为 ${selectedPatient?.name || ''} 创建预约`}
        visible={createAppointmentModalVisible}
        onCancel={() => {
          setCreateAppointmentModalVisible(false);
          appointmentForm.resetFields();
          setSelectedPatient(null);
        }}
        onOk={() => appointmentForm.submit()}
        okText="创建"
        cancelText="取消"
        confirmLoading={schedulesLoading}
      >
        <Form form={appointmentForm} layout="vertical" onFinish={handleAppointmentSubmit}>
          <Form.Item
            name="doctor_schedule_id"
            label="预约时间"
            rules={[{ required: true, message: '请选择预约时间' }]}
          >
            <Select 
              placeholder="请选择预约时间" 
              loading={schedulesLoading}
              showSearch
              optionFilterProp="children"
            >
              {availableSchedules.map(schedule => (
                <Option 
                  key={schedule.id} 
                  value={schedule.id}
                >
                  {`${schedule.schedule_location?.name || '地点未定'}\u2003${dayjs(schedule.start_time).format('YYYY-MM-DD HH:mm')} - ${dayjs(schedule.end_time).format('HH:mm')}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="预约描述"
            rules={[{ required: true, message: '请输入预约描述' }]}
          >
            <Input.TextArea placeholder="请输入预约描述" autoSize={{ minRows: 3, maxRows: 6 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Patients;